import { Document, Page, pdfjs } from "react-pdf";
import { PageCallback } from "react-pdf/dist/cjs/shared/types";
import { PDFDocumentProxy } from "pdfjs-dist/types/src/display/api";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { BrowserCheck } from "@/Common/Components/BrowserCheck";
import { AsyncLoaderControl } from "@/Common/Components/Loader/AsyncLoaderControl";
import { PdfViewerPageToolbar } from "./PdfViewerPageToolbar";
import { PdfViewerViewToolbar } from "./PdfViewerViewToolbar";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "./PdfViewer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.js",
    import.meta.url
).toString();

interface PdfViewerProps {
    pdfSource?: string;
    selectedPages?: PdfPageDetails[];
    pagesWithDetails?: PdfPageDetails[];
    selectedZoomLevelIndex?: number;
    selectedZoomRatio?: number;
    isDeletable?: boolean;
    isReadOnly?: boolean;
    shouldShowPopoutInViewToolbar?: boolean;
    onPageSelectionChange?: (selectedPages: PdfPageDetails[]) => void;
    onTrashPage?: (changedPageDetails: PdfPageDetails) => void;
    onPageOrientationChange?: (pageDetails: PdfPageDetails) => void;
    onZoomLevelChanged?: (zoomFactor: number) => void;
    onTrashDocument?: () => void;
    onClearSelection?: () => void;
    onSelectAllSelection?: () => void;
    onPopoutPdfPage?: (pageNumber: number) => void;
    onLoadDocumentSuccess?: (pageNumber: number) => void;
    onLoadDocumentFail?: (errorName?: string, errorMessage?: string) => void;
    onSetDefaultZoomLevel?: (currentZoomLevels: number[], defaultPageWidth: number) => void;
    onPopoutPdf?: () => void;
    onLoadPageSuccess?: (pageNumber: number, pageOrientation: number) => void;
    hideToolbar?: boolean;
}

class PdfViewerState {
    public pageCount: number = 0;
    public pageRatios: number[] = [];
    public shouldReLoaded: boolean = false;
    public visiblePages: boolean[] = [];
    public pageRefs: React.RefObject<HTMLDivElement>[] = [];
}

class PdfViewer extends BaseReactComponent<PdfViewerProps, PdfViewerState> {
    private viewScaleLevels = [0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.15, 1.3];
    private pageWidth: number = 1000;
    private intersectionObserver: IntersectionObserver;
    private rootRef: React.RefObject<HTMLDivElement>;

    constructor(props: PdfViewerProps) {
        super(props);
        this.state = new PdfViewerState();
        this.rootRef = React.createRef<HTMLDivElement>();
    }

    public componentDidMount(): void {
        const observerOptions = {
            root: this.rootRef.current,
            rootMargin: "0px",
            threshold: 0
        };
        this.intersectionObserver = new IntersectionObserver(entries => this.intersectionObserverCallback(entries), observerOptions);
    }

    public componentWillUnmount(): void {
        this.intersectionObserver?.disconnect();
    }

    public componentDidUpdate(prevProps: PdfViewerProps, prevState: PdfViewerState): void {
        if (prevProps.pdfSource !== this.props.pdfSource) {
            this.mutateState(s => {
                s.pageCount = 0;
                s.pageRatios = Array(s.pageCount).fill(1);
                s.shouldReLoaded = false;
            });
        }

        if (prevState.pageRefs !== this.state.pageRefs) {
            this.state.pageRefs.forEach(pageRef => {
                if (pageRef?.current) {
                    this.intersectionObserver.observe(pageRef.current);
                }
            });
        }
    }

    public shouldComponentUpdate(nextProps: PdfViewerProps, nextState: PdfViewerState): boolean {
        return nextProps.pdfSource != this.props.pdfSource ||
            nextProps.selectedPages != this.props.selectedPages ||
            nextProps.pagesWithDetails != this.props.pagesWithDetails ||
            nextProps.selectedZoomLevelIndex != this.props.selectedZoomLevelIndex ||
            nextProps.selectedZoomRatio != this.props.selectedZoomRatio ||
            nextProps.isDeletable != this.props.isDeletable ||
            nextProps.isReadOnly != this.props.isReadOnly ||
            nextProps.shouldShowPopoutInViewToolbar != this.props.shouldShowPopoutInViewToolbar ||
            nextProps.hideToolbar != this.props.hideToolbar ||
            nextState.pageCount != this.state.pageCount ||
            nextState.pageRatios != this.state.pageRatios ||
            nextState.shouldReLoaded != this.state.shouldReLoaded ||
            this.state.visiblePages !== nextState.visiblePages ||
            this.state.pageRefs !== nextState.pageRefs
        ;
    }

    public render(): JSX.Element {
        return (
            <div className={`pdfViewer ${this.props?.isReadOnly ? "pdfViewer-readonly" : ""}`}>
                <Document
                    file={this.props.pdfSource}
                    onLoadSuccess={document => {
                        this.handleDocumentLoadSuccess(document);
                    }}
                    onLoadError={() => {
                        console.error("This document could not be loaded in the PDF Viewer due to unsupported format.");
                        this.props?.onLoadDocumentFail?.();
                    }}
                    loading={<AsyncLoaderControl IsActive={true} />}
                    error={(
                        <div className="pdfViewer-error">
                            {cacheStore.GetString("FailedToLoadPdfLabel")}
                            <BrowserCheck
                                chromeMinimumVersion={92}
                                edgeMinimumVersion={92}
                                firefoxMinimumVersion={90}
                                safariMinimumVersion={15}
                            />
                        </div>
                    )}
                >
                    {this.props.hideToolbar || this.renderPdfViewerViewToolbar()}
                    <div
                        ref={this.rootRef}
                        className="main"
                        style={this.props?.isReadOnly ? { maxHeight: "none" } : {}}
                    >
                        <div className="scrollControl">
                            {this.props?.isReadOnly ? this.renderPagesReadonly() : this.renderPages()}
                        </div>
                    </div>
                </Document>
            </div>
        );
    }

    private renderPages(): JSX.Element[] {
        const renderedPages: JSX.Element[] = [];

        if (!this.props.pagesWithDetails) return renderedPages;

        for (let index = 0; index < this.state.pageCount; index++) {
            const currentPageDetails = this.props.pagesWithDetails.find(p => p.pageNumber === index + 1);

            if (!!currentPageDetails && !currentPageDetails.isTrashed && !currentPageDetails.isAssigned) {
                const width = Math.round(this.pageWidth * this.getViewScale());
                const height = (currentPageDetails.orientation === 0 || currentPageDetails.orientation === 180)
                    ? Math.round(width / this.state.pageRatios[index] ?? 1)
                    : Math.round(width / (1 / this.state.pageRatios[index]) ?? 1);

                renderedPages.push(
                    <div
                        ref={this.state.pageRefs[index]}
                        key={`page_${currentPageDetails.pageNumber}`}
                        style={{ width: `${width}px`, height: `${height}px` }}
                        className={`pdf-viewer-page-container${this.props.selectedPages.any(pageDetails => pageDetails.pageNumber === currentPageDetails.pageNumber) ? " selected" : ""}`}
                        onClick={() => this.onSelectPage(currentPageDetails)}
                        data-page-index={index}
                    >
                        {this.state.visiblePages[index] && (
                            <>
                                <div className="page-number">
                                    <label>{index + 1}</label>
                                </div>
                                <div className="pageScaler">
                                    <Page
                                        key={currentPageDetails.pageNumber}
                                        pageNumber={index + 1}
                                        onLoadSuccess={page => {
                                            this.handlePageLoadSuccess(page, index);
                                        }}
                                        rotate={currentPageDetails.orientation}
                                        width={width}
                                        renderAnnotationLayer={false}
                                    />
                                </div>
                                <PdfViewerPageToolbar
                                    onRotate={newOrientation => this.updatePageOrientation(currentPageDetails, newOrientation)}
                                    pageOrientation={currentPageDetails.orientation}
                                    onTrashed={() => this.trashPage(currentPageDetails)}
                                    onOpenPdfPreview={() => this.handleOnExtractPdf(index + 1)}
                                    isDeletable={this.props.isDeletable}
                                />
                            </>
                        )}
                    </div>
                );
            }
        }

        return renderedPages;
    }

    private renderPagesReadonly(): JSX.Element[] {
        const pages: JSX.Element[] = [];
        const zoomedWidth: number = Math.round(this.pageWidth * this.getViewScale());

        for (let i = 0; i < this.state.pageCount; i++) {
            pages.push(
                <div
                    key={i}
                    ref={this.state.pageRefs[i]}
                    data-page-index={i}
                    className="margin-sm-bottom"
                    style={{ width: zoomedWidth + "px", height: Math.round(zoomedWidth / (this.state.pageRatios?.[i] ?? 1)) + "px" }}
                >
                    {this.state.visiblePages[i] && (
                        <Page
                            pageNumber={i + 1}
                            width={zoomedWidth}
                            onLoadSuccess={page => {
                                if (page?.view?.length > 3) {
                                    const rotate = page?.rotate ?? 0;
                                    const pageNumber = page?.pageNumber ?? -1;

                                    this.props.onLoadPageSuccess?.(pageNumber, rotate);

                                    this.mutateState(s => {
                                        const calculatedRatio = page.view[2] / page.view[3];
                                        s.pageRatios[i] = (rotate === 0 || rotate === 180) ? calculatedRatio : (1 / calculatedRatio);
                                    });
                                }
                            }}
                            onLoadError={(error: Error) => this.props.onLoadDocumentFail?.(error.name, error.message)}
                            onRenderError={(error: Error) => this.props.onLoadDocumentFail?.(error.name, error.message)}
                            renderAnnotationLayer={false}
                        />
                    )}
                </div>
            );
        }

        return pages;
    }

    private renderPdfViewerViewToolbar(): JSX.Element {
        return this.props?.isReadOnly ? (
            <PdfViewerViewToolbar
                onZoomLevelChange={zoomChange => this.props.onZoomLevelChanged(this.getNextZoomIndex(zoomChange))}
                shouldShowPopout={this.props.shouldShowPopoutInViewToolbar}
                onPopoutPdf={() => this.props.onPopoutPdf?.()}
                isReadOnly
            />
        ) : (
            <PdfViewerViewToolbar
                onZoomLevelChange={zoomChange => this.props.onZoomLevelChanged(this.getNextZoomIndex(zoomChange))}
                onTrashDocument={() => this.props.onTrashDocument()}
                onSelectAllSelection={() => this.props.onSelectAllSelection()}
                onClearSelection={() => this.props.onClearSelection()}
                selectedPageNumbers={this.props.selectedPages.map(p => p.pageNumber)}
                isDeletable={this.props.isDeletable}
            />
        );
    }

    private handleOnExtractPdf(pageNumber: number): void {
        return this.props.onPopoutPdfPage(pageNumber);
    }

    private updatePageOrientation(pageDetails: PdfPageDetails, newOrientation: number): void {
        this.props.onPageOrientationChange({ ...pageDetails, orientation: newOrientation });
    }

    private trashPage(pageDetails: PdfPageDetails): void {
        this.props.onTrashPage({ ...pageDetails, isTrashed: true });
    }

    private getNextZoomIndex(zoomChange: number): number {
        return this.normalizeZoomIndex(this.props.selectedZoomLevelIndex + zoomChange);
    }

    private getViewScale(): number {
        return (
            this.props.selectedZoomRatio
            || this.viewScaleLevels[this.normalizeZoomIndex(this.props.selectedZoomLevelIndex)]
        );
    }

    private normalizeZoomIndex(index: number): number {
        return index = index < 0 ? 0 : index > this.viewScaleLevels.length - 1 ? this.viewScaleLevels.length - 1 : index;
    }

    private handleDocumentLoadSuccess(document: PDFDocumentProxy): void {
        const { numPages } = document;

        if (!IsNullOrUndefined(numPages)) {
            this.mutateState(s => {
                s.pageCount = numPages;
                s.visiblePages = Array.from(new Array(numPages), () => false);
                s.pageRefs = Array.from(new Array(numPages), () => React.createRef());
            });
            this.props?.onLoadDocumentSuccess?.(numPages);
            this.props?.onSetDefaultZoomLevel?.([...this.viewScaleLevels], this.pageWidth);

            // Prevent text selection after the PDF is loaded
            this.preventPageSelectionToggleWhenSelectingText();
        }
    }

    private handlePageLoadSuccess(page: PageCallback, index: number): void {
        const loadedPageWidth = page.view[2];
        const loadedPageHeight = page.view[3];

        if (!IsNullOrUndefined(loadedPageWidth) && !IsNullOrUndefined(loadedPageHeight)) {
            const calculatedRatio = loadedPageWidth / loadedPageHeight;
            const updatedPageRatios = [...this.state.pageRatios];

            updatedPageRatios[index] = calculatedRatio;

            this.mutateState(s => s.pageRatios = updatedPageRatios);
        }
    }

    private preventPageSelectionToggleWhenSelectingText(): void {
        setTimeout(() => {
            const textElements = document.getElementsByClassName("react-pdf__Page__textContent");
            for (let textElement of Array.from(textElements)) {
                textElement.addEventListener("click", event => event.stopPropagation());
            }

            this.mutateState(s => s.shouldReLoaded = textElements.length > 0 && !this.state.shouldReLoaded);
        }, 100);
    }

    private onSelectPage(currentPageDetails: PdfPageDetails): void {
        const selectedPages =
            this.props.selectedPages.any(pageDetails => pageDetails.pageNumber === currentPageDetails.pageNumber)
                ? this.props.selectedPages.filter(page => page.pageNumber !== currentPageDetails.pageNumber)
                : [...this.props.selectedPages, currentPageDetails];

        this.props.onPageSelectionChange(selectedPages);
    }

    private intersectionObserverCallback(entries: IntersectionObserverEntry[]): void {
        const intersections = entries.reduce((acc, entry) => {
            const pageIndex = parseInt(entry.target.getAttribute("data-page-index"));

            return {
                ...acc,
                [pageIndex]: entry.isIntersecting
            };
        }, {});

        const updatedVisiblePages = this.state.visiblePages.map((visiblePage, index) => {
            if (intersections.hasOwnProperty(index)) {
                return intersections[index];
            }
            else {
                return visiblePage;
            }
        });

        this.mutateState(s => s.visiblePages = updatedVisiblePages);
    }
}

class PdfPageDetails {
    public isTrashed: boolean = false;
    public isAssigned: boolean = false;
    public orientation: number = 0;
    public pageNumber: number = 0;

    constructor(fields?: Partial<PdfPageDetails>) {
        Object.assign(this, fields);
    }

    /**
     * Compare and return the updated value if the original and updated values' page number is equal
     * @param originalValue
     * @param updatedValue
     * @returns {PdfPageDetails} returns the update value if the original and updated values' page number is equal If not, returns the original value
     */
    public static getUpdatedPdfPageIfPageNumberIsEqual(originalValue: Readonly<PdfPageDetails>, updatedValue: Readonly<PdfPageDetails>): PdfPageDetails {
        if (originalValue.pageNumber === updatedValue.pageNumber) {
            return new PdfPageDetails({ ...updatedValue });
        }
        else {
            return new PdfPageDetails({ ...originalValue });
        }
    }
}

export { PdfPageDetails, PdfViewer, PdfViewerProps };
